import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MainLayout from 'src/components/layouts/MainLayout';
import styles from 'src/styles/layout.module.css';
import { Breadcrumb, Button, Divider, Image, Layout, Row, Select, Space, Spin, Typography, Upload } from 'antd';
import { Link, NavLink, Redirect, Route, Switch, useHistory, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { DeleteOutlined, PlaySquareTwoTone, ProfileTwoTone } from '@ant-design/icons';
import { AppDispatch } from 'src/index';
import ConfirmButton from 'src/components/atoms/ConfirmButton';
import SwitchSelectField from 'src/components/fields/SwitchSelectField';
import { useContentById, useContentImage } from 'src/features/contents/contents.hooks';
import { deleteContent, fetchContent, updateContent } from 'src/features/contents/contents.state';
import { ContentCard, VRType } from 'src/features/contents/contents.types';
import { formatDatetime } from 'src/helpers/formatters';
import { FormattedMessage } from 'react-intl';
import localStyles from 'src/components/pages/Company/CompanyPage/CompanyPage.module.css';
import RouteProtected from 'src/components/blocks/RouteProtected/RouteProtected';
import ContentCardCollectionsTab from 'src/components/pages/Content/ContentCardPage/ContentCardCollectionsTab';
import ReactPlayer from 'react-player';
import InputEditableField from 'src/components/fields/InputEditableField';
import { UploadFile } from 'antd/es/upload/interface';
import InputNumberEditableField from 'src/components/fields/InputNumberEditableField';
import { RoleEnum } from '../../../../features/users/users.types';
import ContentCardLocalesTab from './ContentCardLocalesTab';
import ContentCardDetailsTab from './ContentCardDetailsTab';
import { useDevicesTypes } from '../../../../features/devicesTypes/devicesTypes.hooks';
import EditableField from '../../../fields/EditableField';
import { fetchDeviceTypes } from '../../../../features/devicesTypes/devicesTypes.state';

const { Content } = Layout;

const ContentCardPage: React.FC = () => {
  const { contentId } = useParams() as { contentId: string };
  const dispatch: AppDispatch = useDispatch();
  const content = useContentById(contentId) as ContentCard;
  const history = useHistory();
  const { uploadContentImage } = useContentImage({
    contentId,
  });
  const deviceTypes = useDevicesTypes();
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);

  useEffect(() => {
    setSelectedTypes(content.devices || []);
  }, [content.devices]);
  const handleUpload = useCallback(
    (file: UploadFile, imageType: 'ver' | 'hor') => {
      uploadContentImage(file as unknown as File, imageType);
    },
    [uploadContentImage]
  );

  const handleDeleteButtonClick = useCallback(async () => {
    const response = await dispatch(deleteContent({ contentId }));
    if (deleteContent.fulfilled.match(response)) {
      history.push(`/content/cards`);
    }
  }, [dispatch, contentId, history]);
  const handleCardUpdate = useCallback(
    (data: Partial<ContentCard>) => {
      dispatch(updateContent({ contentId, data }));
    },
    [contentId, dispatch]
  );

  return (
    <MainLayout>
      <div className={styles.mainGrid}>
        <div className={styles.breadCrumbs}>
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to='/content/cards'>
                <Space>
                  <PlaySquareTwoTone />
                  Карточки контента
                </Space>
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to={`/content/cards/${contentId}`}>{content.name}</Link>
            </Breadcrumb.Item>
          </Breadcrumb>
        </div>
        <div className={styles.contentArea}>
          <Space direction={'vertical'} size={16} style={{ width: '100%' }}>
            <InputEditableField level={2} value={content.name} onBlur={e => handleCardUpdate({ name: e })} />
            <Space style={{ width: '100%', marginBottom: 20 }} direction={'vertical'}>
              <ReactPlayer url={content.cloudUrl} controls={true} />
            </Space>
            <Space size='small' wrap>
              <NavLink to={`details`} activeClassName={localStyles.activeLink}>
                <Button size='middle'>Детали</Button>
              </NavLink>
              <NavLink to={`collections`} activeClassName={localStyles.activeLink}>
                <Button size='middle'>Коллекции</Button>
              </NavLink>
              <NavLink to={`locales`} activeClassName={localStyles.activeLink}>
                <Button size='middle'>Переводы</Button>
              </NavLink>
            </Space>
            <Content>
              <Route
                render={({ match: { path } }) => (
                  <Switch>
                    <RouteProtected
                      exact
                      path={`${path}/collections`}
                      component={ContentCardCollectionsTab}
                      roles={[RoleEnum.admin, RoleEnum.observer]}
                    />
                    <RouteProtected
                      exact
                      path={`${path}/details`}
                      component={ContentCardDetailsTab}
                      roles={[RoleEnum.admin, RoleEnum.observer]}
                    />
                    <RouteProtected
                      exact
                      path={`${path}/locales`}
                      component={ContentCardLocalesTab}
                      roles={[RoleEnum.admin, RoleEnum.observer]}
                    />
                    <Redirect from={path} to={`${path}/details`} exact />
                  </Switch>
                )}
              />
            </Content>
          </Space>
        </div>
        <div className={styles.sidebar}>
          <Space direction={'vertical'} style={{ width: '100%' }}>
            {content.image && <Image src={content.image} width={160} />}
            <Upload
              beforeUpload={() => false}
              accept={'image/*'}
              showUploadList={false}
              onChange={e => handleUpload(e.file, 'hor')}
            >
              <Button type={'primary'}>{content.image ? 'Обновить постер' : 'Загрузить постер'}</Button>
            </Upload>
            {content.imageVer && <Image src={content.imageVer} width={160} />}
            <Upload
              beforeUpload={() => false}
              accept={'image/*'}
              showUploadList={false}
              onChange={e => handleUpload(e.file, 'ver')}
            >
              <Button type={'primary'}>
                {content.imageVer ? 'Обновить вертикальный постер' : 'Загрузить вертикальный постер'}
              </Button>
            </Upload>
            <div className={styles.propertiesGrid}>
              <Typography.Text className={styles.title}>IMDB</Typography.Text>
              <InputEditableField
                value={content.ratingIndicators?.imdb as string}
                onBlur={e =>
                  handleCardUpdate({
                    ratingIndicators: {
                      ...(content.ratingIndicators || {}),
                      imdb: e as string,
                    },
                  })
                }
              />
              <Typography.Text className={styles.title}>Кинопоиск</Typography.Text>
              <InputEditableField
                value={content.ratingIndicators?.kinopoisk as string}
                onBlur={e =>
                  handleCardUpdate({
                    ratingIndicators: {
                      ...(content.ratingIndicators || {}),
                      kinopoisk: e as string,
                    },
                  })
                }
              />
              <Typography.Text className={styles.title}>Длительность в мин</Typography.Text>
              <InputNumberEditableField value={content.duration || 0} onBlur={e => handleCardUpdate({ duration: e })} />
              <Typography.Text className={styles.title}>Бесплатно</Typography.Text>
              <SwitchSelectField value={content.free} onSelect={e => handleCardUpdate({ free: e })} />
              <Typography.Text className={styles.title}>Типы устройств</Typography.Text>
              <Select
                value={selectedTypes || []}
                placeholder='Выберите тип шлема'
                onBlur={() => handleCardUpdate({ devices: selectedTypes })}
                onChange={e => setSelectedTypes(e)}
                mode={'multiple'}
                style={{ width: '100%' }}
              >
                {deviceTypes.map(l => (
                  <Select.Option key={l.name} value={l.name}>
                    {l.name}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </Space>
          <Divider />
          <Space direction='vertical' size='small'>
            <Typography.Text className={styles.footerDateStamp}>
              <FormattedMessage id='field.createdAt' />: {formatDatetime(content.createdAt)}
            </Typography.Text>
            <Typography.Text className={styles.footerDateStamp}>
              <FormattedMessage id='field.updatedAt' />: {formatDatetime(content.updatedAt)}
            </Typography.Text>
            <ConfirmButton
              type='link'
              onClick={handleDeleteButtonClick}
              danger
              size='small'
              style={{ padding: 0 }}
              disabled={content.archived}
            >
              <DeleteOutlined /> Удалить карточку
            </ConfirmButton>
          </Space>
        </div>
      </div>
    </MainLayout>
  );
};

const ContentCardPageBlock: React.FC = () => {
  const { contentId } = useParams() as { contentId: string };
  const dispatch = useDispatch();
  const category = useContentById(contentId);

  useEffect(() => {
    dispatch(fetchDeviceTypes({}));
    dispatch(fetchContent({ contentId }));
  }, [dispatch, contentId]);

  if (!category) {
    return (
      <Row align={'middle'} justify={'center'} style={{ minHeight: '100vh' }}>
        <Spin size={'large'} />
      </Row>
    );
  }

  return <ContentCardPage />;
};
export default ContentCardPageBlock;
